import { apiGetCardPin, selectedCards } from '@shared-core/features/backend'
import { useNotifications } from '@shared-core/features/notifications'
import { useI18N } from '@shared-core/hooks/use-i18n'
import { type RootRouteProps, ScreensList, useFocusEffect, useNavigation, useRoute } from '@shared-core/navigation'
import { useAppSelector } from '@shared-core/store'
import { Button } from '@shared-ui/components/ui/button'
import { Column } from '@shared-ui/components/ui/grid'
import { Header } from '@shared-ui/components/ui/header'
import { ScreenWrapperWithoutScroll } from '@shared-ui/components/ui/screen-wrappers/screen-wrapper'
import { GText } from '@shared-ui/components/ui/text'
import { useHardwareBackPress } from '@shared-ui/hooks/rn/use-hardware-back-press'
import { type CardPinKeyboardProps, ScreenCardsPinSetupDirect } from '@shared-ui/screens/cards/pin-setup/direct'
import { CardPinProvider } from '@shared-ui/screens/cards/pin-setup/provider'
import { type ComponentType, useCallback, useRef, useState } from 'react'
import locales from './index.i18n.json'
import { useCreateStyles } from './styles'

export type ScreenCardsPinSetupProps = { KeyboardComponent: ComponentType<CardPinKeyboardProps> }

export function ScreenCardsPinSetup({ KeyboardComponent }: ScreenCardsPinSetupProps) {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)
  const navigation = useNavigation()
  const route = useRoute<RootRouteProps<ScreensList.CARD_PIN_SETUP>>()
  const cardId = route.params?.cardId
  const cards = useAppSelector(selectedCards)
  const { addToastSuccess } = useNotifications()
  const [url, setUrl] = useState<string | null>(null)
  const [mode, setMode] = useState<1 | 2 | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [attempt, setAttempt] = useState(0)
  const completedRef = useRef(false)

  // Ссылку провайдера запрашиваем заново при повторной попытке; после ухода ответ игнорируем.
  useFocusEffect(
    useCallback(() => {
      let active = true
      const controller = new AbortController()
      setIsLoading(true)
      setError('')
      setMode(null)
      setIsOpen(false)
      setUrl(null)
      completedRef.current = false
      // Режим определяет, где вводится PIN: в приложении или на странице эмитента.
      void (async () => {
        try {
          if (!cardId) throw new Error('Missing card ID')

          const response = await apiGetCardPin({ routeParams: { id: cardId }, signal: controller.signal })
          const pin = response?.data

          if (!active) return

          if (response?.status !== 'ok' || !pin) {
            const codes = Object.values(response?.errors ?? {}).flat()
            let message = t('provider-unavailable')

            if (codes.includes('CARD_NOT_FOUND')) message = t('card-not-found')

            if (codes.includes('INVALID_CARD_STATUS')) message = t('card-inactive')

            if (codes.includes('PIN_UNAVAILABLE')) message = t('pin-unavailable')
            setError(message)
            return
          }

          if (pin.mode === 2 && !/^https?:\/\//i.test(pin.url)) throw new Error('Invalid PIN provider URL')

          setMode(pin.mode)
          setUrl(pin.url)
        } catch {
          if (active) setError(t('provider-unavailable'))
        } finally {
          if (active) setIsLoading(false)
        }
      })()
      return () => {
        active = false
        controller.abort()
      }
    }, [cardId, attempt, t]),
  )

  const handleSuccess = () => {
    if (completedRef.current) return
    completedRef.current = true
    setIsOpen(false)
    addToastSuccess(t('success'))
    const card = cards?.find((item) => item.id === cardId)

    // История iframe провайдера может перехватить обычный переход назад.
    if (card) navigation.replace(ScreensList.CARD_DETAILS, { cardId: card.id, templateId: card.templateId })
    else navigation.replace(ScreensList.HOME_START)
  }

  useHardwareBackPress(() => {
    if (!isOpen) return false

    setIsOpen(false)
    return true
  }, [isOpen])

  let content = (
    <>
      <Column style={styles.content}>
        <GText size={24} fw={600} align="center">
          {t('title')}
        </GText>
        <GText size={16} isSecondary align="center">
          {error || t('description')}
        </GText>
      </Column>
      <Column style={styles.action}>
        <Button
          isLoading={isLoading}
          disabled={isLoading}
          onPress={() => {
            if (url) setIsOpen(true)
            else setAttempt((value) => value + 1)
          }}
        >
          {t('go')}
        </Button>
      </Column>
    </>
  )

  if (mode === 1 && cardId) {
    content = (
      <ScreenCardsPinSetupDirect
        KeyboardComponent={KeyboardComponent}
        key={cardId}
        cardId={cardId}
        onSuccess={handleSuccess}
        onReload={() => setAttempt((value) => value + 1)}
      />
    )
  }

  if (isOpen && url) {
    content = (
      <CardPinProvider
        url={url}
        onSuccess={handleSuccess}
        onError={() => {
          setIsOpen(false)
          setError(t('webview-error'))
          setUrl(null)
        }}
      />
    )
  }

  return (
    <ScreenWrapperWithoutScroll style={styles.root}>
      {mode !== 1 && (
        <Header
          isBackButton
          title={t('title')}
          onBackHandler={() => {
            if (!isOpen) return true

            setIsOpen(false)
            return false
          }}
        />
      )}
      {content}
    </ScreenWrapperWithoutScroll>
  )
}
